import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  IconButton,
  Divider,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { TranslationKeys } from '../../../i18n';

interface ExistingProjectWarningModalProps {
  open: boolean;
  projectName: string;
  bcName: string;
  gpm: string;
  bcId: number;
  onCancel: () => void;
}

const ExistingProjectWarningModal: React.FC<ExistingProjectWarningModalProps> = ({
  open,
  projectName,
  bcName,
  gpm,
  bcId,
  onCancel,
}) => {
  const { t } = useTranslation<TranslationKeys>();
  const navigate = useNavigate();

  const handleGoToOverview = () => {
    onCancel();
    navigate(`/business-case/${bcId}`);
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoOutlinedIcon color="info" fontSize="large" />
            <Typography variant="h6" component="div">
              {t('modal.existing_project_title')}
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={onCancel}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          {t('modal.existing_project_alert')}
        </Alert>

        <Box sx={{ 
          backgroundColor: 'grey.50', 
          borderRadius: 2, 
          p: 2, 
          mb: 3,
          border: '1px solid',
          borderColor: 'grey.200'
        }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('modal.existing_project_name_label')}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
            {projectName}
          </Typography>

          <Divider sx={{ my: 1.5 }} />

          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('modal.existing_bc_name_label')}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
            {bcName || '-'}
          </Typography>

          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('modal.existing_gpm_label')}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {gpm || '-'}
          </Typography>
        </Box>

        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            {t('modal.existing_project_version_warning')}
          </Typography>
        </Alert>

        <Typography variant="body2" color="text.secondary">
          {t('modal.existing_project_suggestion')}
        </Typography>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button 
          onClick={onCancel} 
          variant="outlined"
          color="inherit"
        >
          {t('modal.existing_project_cancel')}
        </Button>
        <Button 
          onClick={handleGoToOverview}
          variant="contained" 
          color="primary"
          endIcon={<OpenInNewIcon />}
        >
          {t('modal.existing_project_go_to_overview')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExistingProjectWarningModal;
